import { FormEvent, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { PageCard } from '../../components/layout/page/PageCard';
import { PageHeader } from '../../components/layout/page/PageHeader';
import { PageShell } from '../../components/layout/page/PageShell';
import { SettingsTabBar } from '../../components/settings/SettingsTabBar';
import { SettingsTabItem } from '../../components/settings/SettingsTabItem';
import { LoadingSpinner } from '../../components/ui/LoadingSpinner';
import { PasswordInput } from '../../components/ui/PasswordInput';
import { useAuth } from '../../contexts/AuthContext';
import { changePassword } from '../../services/authApi';
import {
  fetchNotificationPreferences,
  PLANNING_TASK_EVENT_TYPES,
  PLANNING_TASK_PRIORITIES,
  saveNotificationPreferences,
  UserNotificationPreferences,
} from '../../services/notificationPreferencesApi';
import { fetchProfile, updateProfile } from '../../services/platformApi';
import { MyRegistrationSection } from './MyRegistrationSection';
import { MyAttributionsSection } from './MyAttributionsSection';
import { MyTeamsSection } from './MyTeamsSection';
import { ProfileSettingsTabId } from './ProfileSettingsTabId';

const PROFILE_TABS: SettingsTabItem<ProfileSettingsTabId>[] = [
  { id: 'profile', label: 'Perfil' },
  { id: 'registration', label: 'Cadastro' },
  { id: 'attributions', label: 'Atribuições' },
  { id: 'teams', label: 'Equipes' },
  { id: 'indicators', label: 'Meus Indicadores' },
  { id: 'notifications', label: 'Notificações' },
];

const PRIORITY_LABELS: Record<string, string> = {
  Low: 'Baixa',
  Medium: 'Média',
  High: 'Alta',
  Critical: 'Crítica',
};

export function ProfileSettingsPage() {
  const { refreshProfile, profile } = useAuth();
  const [activeTab, setActiveTab] = useState<ProfileSettingsTabId>('profile');
  const [nickName, setNickName] = useState('');
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [notificationPrefs, setNotificationPrefs] = useState<UserNotificationPreferences | null>(null);
  const [loading, setLoading] = useState(true);
  const [savingProfile, setSavingProfile] = useState(false);
  const [savingPassword, setSavingPassword] = useState(false);
  const [savingNotifications, setSavingNotifications] = useState(false);

  useEffect(() => {
    async function load() {
      try {
        const [profileData, prefs] = await Promise.all([fetchProfile(), fetchNotificationPreferences()]);
        setNickName(profileData.nickName);
        setNotificationPrefs(prefs);
      } finally {
        setLoading(false);
      }
    }

    void load();
  }, []);

  useEffect(() => {
    if (window.location.hash === '#password') {
      setActiveTab('profile');
    }
  }, []);

  async function handleProfileSubmit(event: FormEvent) {
    event.preventDefault();
    setSavingProfile(true);
    try {
      await updateProfile({ nickName: nickName.trim(), profilePhotoBase64: '' });
      await refreshProfile();
      toast.success('Perfil atualizado.');
    } catch {
      toast.error('Não foi possível atualizar o perfil.');
    } finally {
      setSavingProfile(false);
    }
  }

  async function handlePasswordSubmit(event: FormEvent) {
    event.preventDefault();
    setSavingPassword(true);
    try {
      await changePassword(currentPassword, newPassword, confirmNewPassword);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmNewPassword('');
      toast.success('Senha alterada.');
    } catch {
      toast.error('Não foi possível alterar a senha.');
    } finally {
      setSavingPassword(false);
    }
  }

  async function handleNotificationsSubmit(event: FormEvent) {
    event.preventDefault();
    if (!notificationPrefs) return;

    setSavingNotifications(true);
    try {
      setNotificationPrefs(await saveNotificationPreferences(notificationPrefs));
      toast.success('Preferências de notificação salvas.');
    } catch {
      toast.error('Não foi possível salvar as preferências.');
    } finally {
      setSavingNotifications(false);
    }
  }

  function togglePriority(priority: string): void {
    if (!notificationPrefs) return;
    const exists = notificationPrefs.taskPriorities.includes(priority);
    setNotificationPrefs({
      ...notificationPrefs,
      taskPriorities: exists
        ? notificationPrefs.taskPriorities.filter((item) => item !== priority)
        : [...notificationPrefs.taskPriorities, priority],
    });
  }

  function toggleEventType(eventType: string): void {
    if (!notificationPrefs) return;
    const exists = notificationPrefs.taskEventTypes.includes(eventType);
    setNotificationPrefs({
      ...notificationPrefs,
      taskEventTypes: exists
        ? notificationPrefs.taskEventTypes.filter((item) => item !== eventType)
        : [...notificationPrefs.taskEventTypes, eventType],
    });
  }

  if (loading || !notificationPrefs) {
    return <LoadingSpinner />;
  }

  return (
    <PageShell variant="form">
      <PageHeader title="Meu perfil" description={profile?.email ?? undefined} />

      <SettingsTabBar tabs={PROFILE_TABS} activeTab={activeTab} onChange={setActiveTab} />

      {activeTab === 'profile' ? (
        <>
          <PageCard title="Dados de exibição">
            <form onSubmit={(e) => void handleProfileSubmit(e)} className="erp-form">
              <label className="erp-label">
                Nome de exibição
                <input
                  value={nickName}
                  onChange={(e) => setNickName(e.target.value)}
                  className="erp-input"
                  placeholder="Nome de exibição"
                />
              </label>
              <div className="erp-btn-actions">
                <button type="submit" disabled={savingProfile} className="erp-btn-primary">
                  Salvar perfil
                </button>
              </div>
            </form>
          </PageCard>

          <PageCard title="Alterar senha">
            <form id="password" onSubmit={(e) => void handlePasswordSubmit(e)} className="erp-form">
              <label className="erp-label">
                Senha atual
                <PasswordInput
                  autoComplete="current-password"
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  className="erp-input pr-11 !mt-0"
                  wrapperClassName="mt-1 w-full"
                />
              </label>
              <label className="erp-label">
                Nova senha
                <PasswordInput
                  autoComplete="new-password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="erp-input pr-11 !mt-0"
                  wrapperClassName="mt-1 w-full"
                />
              </label>
              <label className="erp-label">
                Confirmar nova senha
                <PasswordInput
                  autoComplete="new-password"
                  value={confirmNewPassword}
                  onChange={(e) => setConfirmNewPassword(e.target.value)}
                  className="erp-input pr-11 !mt-0"
                  wrapperClassName="mt-1 w-full"
                />
              </label>
              <div className="erp-btn-actions">
                <button
                  type="submit"
                  disabled={savingPassword}
                  className="erp-btn-primary"
                >
                  Alterar senha
                </button>
              </div>
            </form>
          </PageCard>
        </>
      ) : null}

      {activeTab === 'registration' ? <MyRegistrationSection /> : null}

      {activeTab === 'attributions' ? <MyAttributionsSection /> : null}

      {activeTab === 'teams' ? <MyTeamsSection /> : null}

      {activeTab === 'indicators' ? (
        <PageCard
          title="Meus indicadores"
          subtitle="Indicadores de desempenho em que você é avaliado, conforme o nível do cargo."
        >
          <p className="rounded-lg border border-dashed border-secondary-200 bg-secondary-50 px-4 py-6 text-center text-sm text-secondary-500">
            Em breve — os indicadores de desempenho ainda não estão disponíveis.
          </p>
        </PageCard>
      ) : null}

      {activeTab === 'notifications' ? (
        <PageCard
          title="Notificações de tarefas"
          subtitle="Escolha quando deseja ser alertado sobre tarefas do planejamento estratégico."
        >
          <form onSubmit={(e) => void handleNotificationsSubmit(e)} className="erp-form space-y-4">
            <label className="erp-label-inline">
              <input
                type="checkbox"
                checked={notificationPrefs.tasksEnabled}
                onChange={(event) =>
                  setNotificationPrefs({ ...notificationPrefs, tasksEnabled: event.target.checked })
                }
              />
              Receber notificações de tarefas
            </label>
            <label className="erp-label-inline">
              <input
                type="checkbox"
                checked={notificationPrefs.notifyInApp}
                onChange={(event) =>
                  setNotificationPrefs({ ...notificationPrefs, notifyInApp: event.target.checked })
                }
              />
              Notificações no sistema (in-app)
            </label>
            <label className="erp-label-inline">
              <input
                type="checkbox"
                checked={notificationPrefs.notifySubordinateTasks}
                onChange={(event) =>
                  setNotificationPrefs({
                    ...notificationPrefs,
                    notifySubordinateTasks: event.target.checked,
                  })
                }
              />
              Alertas sobre tarefas de subordinados (quando sou chefe)
            </label>

            <div>
              <p className="erp-label mb-2">Prioridades (vazio = todas)</p>
              <div className="flex flex-wrap gap-3">
                {PLANNING_TASK_PRIORITIES.map((priority) => (
                  <label key={priority} className="erp-label-inline">
                    <input
                      type="checkbox"
                      checked={notificationPrefs.taskPriorities.includes(priority)}
                      onChange={() => togglePriority(priority)}
                    />
                    {PRIORITY_LABELS[priority] ?? priority}
                  </label>
                ))}
              </div>
            </div>

            <div>
              <p className="erp-label mb-2">Eventos</p>
              <div className="erp-form-grid md:grid-cols-2">
                {PLANNING_TASK_EVENT_TYPES.map((eventType) => (
                  <label key={eventType.id} className="erp-label-inline">
                    <input
                      type="checkbox"
                      checked={notificationPrefs.taskEventTypes.includes(eventType.id)}
                      onChange={() => toggleEventType(eventType.id)}
                    />
                    {eventType.label}
                  </label>
                ))}
              </div>
            </div>

            <div className="erp-btn-actions">
              <button type="submit" disabled={savingNotifications} className="erp-btn-primary">
                Salvar preferências
              </button>
            </div>
          </form>
        </PageCard>
      ) : null}
    </PageShell>
  );
}
